import Modal from "@/components/modal/Modal";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import {
  useAudiolab,
  useTrackUpload,
} from "@/components/providers/AudiolabProvider/hooks";
import { MAX_TRACK_UPLOAD_BATCH } from "@/lib/config/constants.config";
import { useTracksControllerUploadQuota } from "@/lib/lutely-api/gen/tracks/tracks";
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { parseISO } from "date-fns";
import { FiX } from "react-icons/fi";
import ArtistSelector from "@/components/audiolab/ArtistSelector";
import Step3FileUpload from "./Step3FileUpload";

type UploadWizardModalProps = {
  onClose: () => void;
};

// Songs and Beats are consolidated into a single "Track"; Sound Kits are sunset.
// Every upload is therefore created as a SONG.
const TRACK_TYPE = "SONG" as const;

export default function UploadWizardModal({ onClose }: UploadWizardModalProps) {
  const {
    uploadQueue,
    isUploading,
    errorMessage,
    createTracks,
    handleFilesSelect,
    lockedUploadCreatorId,
    uploadMasteringPreset,
    uploadMaxFiles,
  } = useTrackUpload();

  const {
    data: user,
    hasActiveSubscription,
    isLabelSubscriber,
    canSwitchArtists,
  } = useLoggedInUser();
  const { selectedCreator } = useAudiolab();

  // "Master track" and "Upload track" share this modal; the entry point decides
  // the title and whether the track is routed through mastering.
  const title = uploadMasteringPreset ? "Master track" : "Upload track";

  // An album flow pins the artist: the album already belongs to one, so
  // switching here could only produce a track that album will reject.
  const isCreatorLocked = !!lockedUploadCreatorId;
  const creatorId = lockedUploadCreatorId ?? selectedCreator?.creatorId;

  // Only free-tier users need quota data — paid users get the modal instantly
  const { data: quota, isLoading } = useTracksControllerUploadQuota({
    query: {
      enabled: Boolean(user) && !hasActiveSubscription,
    },
  });

  const showUploadQuota = !hasActiveSubscription && !!quota;
  const isOutOfUploads = showUploadQuota && quota.used >= quota.limit;
  const quotaResetsOn = quota
    ? parseISO(quota.resetsAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })
    : "";
  const remainingUploads = quota ? quota.limit - quota.used : 0;
  const showQuotaMessaging = showUploadQuota && remainingUploads <= 3;

  // Batch uploads are a Pro feature: everyone else picks one file at a time.
  // A metered account is further capped by its remaining uploads, so the API
  // can't reject half the batch.
  const batchCap = Math.min(
    isLabelSubscriber ? MAX_TRACK_UPLOAD_BATCH : 1,
    uploadMaxFiles ?? Infinity,
  );
  const maxFiles = showUploadQuota
    ? Math.max(0, Math.min(batchCap, remainingUploads))
    : batchCap;

  // Selecting/dropping files starts the upload immediately — no separate button.
  const handleFilesDrop = (files: File[]) => {
    if (files.length === 0) return;
    const valid = handleFilesSelect(files, TRACK_TYPE, maxFiles);
    if (valid.length > 0 && creatorId) {
      void createTracks(valid, TRACK_TYPE, creatorId, uploadMasteringPreset);
    }
  };

  if (isLoading) {
    return;
  }

  return (
    <Modal
      hideCloseButton
      open
      size="smd"
      onOpenChange={(open) => !open && !isUploading && onClose()}
      containerClassName=""
      contentClassName="px-5 pt-4 pb-7"
    >
      {/* Header — below l2sm the artist selector wraps onto its own line so the
          title keeps the full width instead of being squeezed beside it. */}
      <Flex
        align="center"
        justify="between"
        wrap="wrap"
        className="mb-4 gap-y-3"
      >
        <Flex align="center" gap="2" className="order-1 min-w-0 flex-1">
          <Heading
            size="5"
            className="font-figtree font-semibold text-blue-900"
          >
            {title}
          </Heading>
        </Flex>

        {canSwitchArtists && !isCreatorLocked && (
          <Flex
            align="center"
            gap="2"
            className="order-3 w-full min-w-0 l2sm:order-2 l2sm:w-auto l2sm:mr-2"
          >
            <Text className="shrink-0 font-open-sans text-[13px] text-text-secondary">
              Upload as
            </Text>
            <ArtistSelector variant="compact" className="max-w-[220px]" />
          </Flex>
        )}

        <Flex align="center" className="order-2 shrink-0 l2sm:order-3">
          {isUploading ? (
            <Tooltip content="Upload in progress">
              <IconButton
                variant="ghost"
                size="2"
                disabled
                className="text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiX className="w-5 h-5" />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              variant="ghost"
              size="2"
              onClick={onClose}
              className="text-gray-600 cursor-pointer"
            >
              <FiX className="w-5 h-5" />
            </IconButton>
          )}
        </Flex>
      </Flex>

      {/* Body: out-of-uploads gate for the free tier, otherwise the uploader */}
      {isOutOfUploads ? (
        <Flex direction="column" align="start" gap="3" className="py-4">
          <Flex direction="column" gap="1">
            <Text className="font-open-sans text-sm font-bold text-error-bold">
              Upload limit reached.
            </Text>
            <Text className="font-open-sans text-sm text-gray-600">
              Resets {quotaResetsOn}. Upgrade for unlimited uploads.
            </Text>
          </Flex>
          <Link to="/upgrade" onClick={onClose}>
            <Button
              size="2"
              radius="full"
              data-testid="upload-wizard-view-plans"
            >
              View Plans
            </Button>
          </Link>
        </Flex>
      ) : (
        <Flex direction="column" gap="3">
          <Step3FileUpload
            queue={uploadQueue}
            onFilesSelect={handleFilesDrop}
            errorMessage={errorMessage}
            maxFiles={maxFiles}
            isUploading={isUploading}
          />
          {showQuotaMessaging && (
            <Text className="font-open-sans text-xs text-gray-600">
              {`${remainingUploads} upload${
                remainingUploads === 1 ? "" : "s"
              } remaining this month.`}
            </Text>
          )}
          {/* Navigating away closes the wizard, which drops the queue and the
              release callback waiting to attach the new tracks. The X button is
              disabled mid-upload for the same reason; this link hides instead,
              since an upgrade prompt is noise while a batch is running. */}
          {!isLabelSubscriber && !isUploading && (
            <Text
              className="font-open-sans text-xs text-gray-600"
              data-testid="upload-wizard-pro-batch-hint"
            >
              Want to upload multiple tracks at once?{" "}
              <Link
                to="/upgrade"
                onClick={onClose}
                className="font-semibold text-blue-600 underline"
              >
                Upgrade to Pro
              </Link>
            </Text>
          )}
        </Flex>
      )}
    </Modal>
  );
}
